/**
 * ExecutionAgent - System Prompt Assembly
 * 
 * Combines all sections into the complete system prompt.
 */

import { SystemPromptBuilder } from '../../../base/systemPrompt';
import type { ToolBase } from '../../../base/toolBase';
import { getIntroSection, EXECUTION_AGENT_INTRO } from './intro';
import { buildToolsSection } from './tools';
import { getSuggestionsSection, EXECUTION_AGENT_SUGGESTIONS } from './suggestions';
import { getContextNoteSection, EXECUTION_AGENT_CONTEXT_NOTE } from './contextNote';
import { getEvaluationSection, EXECUTION_AGENT_EVALUATION } from './evaluation';

// Import shared builder prompt content
import {
    DENSITY_SECTION_FULL,
    CHART_SOPHISTICATION,
    TABLE_SOPHISTICATION,
    STYLE_CONSISTENCY,
    RULES_FOR_SLIDE_CONSTRUCTION,
    ARTIFACT_ID_USAGE,
    generateAvailableSlidesSection,
} from '../../prompts';

export interface ExecutionAgentPromptParams {
    sectionTitle?: string;
    slidesToBuild: Array<{
        actualSlideIndex: number;
        title: string;
        purpose: string;
        areas: Array<{
            areaId: string;
            region: { x: number; y: number; width: number; height: number };
            purpose: string;
            contentType: string;
        }>;
        context: Array<{ context: string[]; purpose: string }>;
    }>;
    slideDimensions: { width: number; height: number };
    styleGuideSlideCount: number;
    overallPlan?: string;
    globalContext?: Array<{ context: string[]; purpose: string }>;
    referenceSlideIndices?: number[];
    excelContext?: {
        workbookPath: string;
        ranges: Array<{ sheetName: string; range: string; description?: string; excelId?: string }>;
    };
    styleGuideInfo?: {
        topStyleSignatures: Array<{
            style?: Record<string, unknown>;
            usage_percentage?: number;
        }>;
    };
    tools: ToolBase[];
    hasImages?: boolean;
    imageIds?: string[];
}

export function buildExecutionAgentSystemPrompt(params: ExecutionAgentPromptParams): string {
    // Build available slides section
    const availableSlidesSection = generateAvailableSlidesSection({
        styleGuideSlideCount: params.styleGuideSlideCount,
        workingSlideCount: params.slidesToBuild.length,
    });
    
    // Build reference slides section
    let referenceStr = '';
    if (params.referenceSlideIndices && params.referenceSlideIndices.length > 0) {
        referenceStr = `
## Reference Slides (for visual styling)

Use these slides as visual references for styling: ${params.referenceSlideIndices.join(', ')}
You can call readSlide on these to see their structure.
`;
    }
    
    // Build Excel context section
    let excelStr = '';
    if (params.excelContext) {
        excelStr = `
## Excel Data Available

- Workbook: ${params.excelContext.workbookPath}
- Ranges: ${params.excelContext.ranges.map(r => `${r.sheetName}!${r.range} (${r.description || r.excelId})`).join(', ')}
`;
    }
    
    // Build global context section
    let globalContextStr = '';
    if (params.globalContext && params.globalContext.length > 0) {
        globalContextStr = `
## Global Context (applies to ALL slides)

${params.globalContext.map(item => `**${item.purpose}**: ${item.context.join(', ')}`).join('\n')}
`;
    }
    
    // Build overall plan context section
    let planContextStr = '';
    if (params.overallPlan) {
        planContextStr = `
## Presentation Plan Context

You are building slides as part of a larger presentation. Here is the full plan:

\`\`\`
${params.overallPlan}
\`\`\`

**Use this context to:**
- Understand how this section fits into the overall narrative
- Avoid repeating content that was covered in previous slides
- Don't preempt content that will be covered in later slides
- Unless the plan explicitly calls for repetition/reinforcement
`;
    }
    
    // Build style pattern section
    const stylePatternSection = buildStylePatternSection(params);
    
    // Build slides section
    const slidesStr = params.slidesToBuild.map((slide, i) => {
        const areasStr = slide.areas.map(area => {
            const pointsRegion = regionToPoints(area.region, params.slideDimensions);
            return `    - **${area.areaId}** (${area.contentType}): ${area.purpose}
      Region: left=${pointsRegion.left}, top=${pointsRegion.top}, width=${pointsRegion.width}, height=${pointsRegion.height} points`;
        }).join('\n');
        
        let contextStr = '';
        if (slide.context.length > 0) {
            contextStr = `  **Context**:
${slide.context.map(ctx => `    - ${ctx.purpose}: ${ctx.context.join(', ')}`).join('\n')}`;
        }
        
        return `### Slide ${i + 1}: ${slide.title} (index ${slide.actualSlideIndex})
  Purpose: ${slide.purpose}
  Areas to build:
${areasStr}
${contextStr}`;
    }).join('\n\n');
    
    // Note about images
    const imageNote = params.hasImages && params.imageIds && params.imageIds.length > 0
        ? `\n\n**Note**: ${params.imageIds.length} context image(s) are provided in the initial message: ${params.imageIds.join(', ')}`
        : '';
    
    // Assemble the prompt
    const sections: Parameters<typeof SystemPromptBuilder.build>[0]['sections'] = {
        intro: getIntroSection() + `

**Current Build Task:**
${params.sectionTitle ? `- Section: "${params.sectionTitle}"` : ''}
- Building ${params.slidesToBuild.length} slide(s)
- Slide dimensions: ${params.slideDimensions.width} x ${params.slideDimensions.height} points

---

${availableSlidesSection}
${planContextStr}
${globalContextStr}
${referenceStr}
${excelStr}

---

${DENSITY_SECTION_FULL}

---

## Chart Sophistication

${CHART_SOPHISTICATION}

---

## Table Sophistication

${TABLE_SOPHISTICATION}

---

${STYLE_CONSISTENCY}
${stylePatternSection}
---

${ARTIFACT_ID_USAGE}

---

## Slides to Build

${slidesStr}

---

## Building Process

For each slide:
1. Use readSlide to see the current state (workingSlides or styleGuideSlides)
2. Build EACH specified area using buildArea
3. Check the screenshot result to verify quality
4. If something looks wrong, call rollback and try again

### Area Placement Rules

Areas are PRE-DEFINED by the orchestrator. Your job is to:
1. Build content WITHIN each area's bounds
2. Perfect the placement (exact positions, sizes, spacing within the area)
3. Ensure shapes don't overlap or exceed area boundaries
4. Use the region as a guide, but optimize for visual balance

Area regions are normalized (0-1) and converted to points for you in the slide list above.

---

## buildArea Schema

The buildArea tool accepts:
- slideIndex: The slide index to build on
- areaDescription: Description of what you're building
- areaSpec: Contains targetArea (bounding box) and collections (shapes to create)

Position values are in POINTS (72 points = 1 inch).
Standard slide is ${params.slideDimensions.width} x ${params.slideDimensions.height} points.

Collection types:
- type: "createShape" for text boxes, rectangles, etc.
- type: "createTable" for tables
- type: "createChart" for charts (link to Excel data)
- type: "copy" to copy shapes from reference slides

---

${RULES_FOR_SLIDE_CONSTRUCTION}

---

## Error Recovery

If a buildArea result looks WRONG in the screenshot:
1. Note the checkpoint ID from the buildArea result
2. Call rollback with that checkpoint ID and reason
3. Try building the area again differently

---

## Completion

When ALL slides are built and look good, call submitResults with:
- One result item per slide (requirementId = "slide_N" where N is the slide index)
- relevantResults containing: slidesBuilt (count), success (boolean)
- summary of what was accomplished
${imageNote}

START by reading the first slide (index ${params.slidesToBuild[0]?.actualSlideIndex ?? 0}) to see its current state.`,
        tools: buildToolsSection(params.tools),
        suggestions: getSuggestionsSection(),
        contextNote: getContextNoteSection(),
        evaluation: getEvaluationSection(),
    };
    
    return SystemPromptBuilder.build({
        sections,
        includeSectionHeaders: false,
        separator: '\n\n',
    });
}

/**
 * Convert normalized region (0-1) to points using actual slide dimensions
 */
function regionToPoints(region: { x: number; y: number; width: number; height: number }, dimensions: { width: number; height: number }): {
    left: number;
    top: number;
    width: number;
    height: number;
} {
    return {
        left: Math.round(region.x * dimensions.width),
        top: Math.round(region.y * dimensions.height),
        width: Math.round(region.width * dimensions.width),
        height: Math.round(region.height * dimensions.height),
    };
}

/**
 * Build style pattern section from styleGuideInfo
 */
function buildStylePatternSection(params: ExecutionAgentPromptParams): string {
    const topStyles = params.styleGuideInfo?.topStyleSignatures;
    
    if (!topStyles || topStyles.length === 0) {
        return '';
    }
    
    const lines: string[] = [
        '',
        '### Detected Style Patterns (from style guide analysis)',
        '',
        'Apply these exact styles for consistency:',
        '',
    ];
    
    for (const sig of topStyles.slice(0, 10)) {
        const style = sig.style || {};
        const font = (style.font_family as string) || '?';
        const size = style.font_size ? `${style.font_size}pt` : '?pt';
        const color = (style.font_color as string) || '';
        const fill = style.fill_color ? ` fill:${style.fill_color}` : '';
        const bold = style.bold ? ' **bold**' : '';
        const usage = style.usage ? ` [${style.usage}]` : '';
        const pct = sig.usage_percentage ? ` (${sig.usage_percentage}%)` : '';
        
        lines.push(`- ${font} ${size}${bold} ${color}${fill}${usage}${pct}`);
    }
    
    lines.push('');
    lines.push('**Match these fonts, sizes, and colors exactly when creating shapes.**');
    lines.push('');
    
    return lines.join('\n');
}

// Variables and prompt configuration
export {
    EXECUTION_AGENT_VARIABLES,
    EXECUTION_AGENT_PROMPT_KEYS,
    EXECUTION_AGENT_PROMPT_CONFIG,
} from './variables';
